import { isPasskeyAlreadyEnrolledError, isPasskeyCanceledError } from 'utils/webAuthn';

import {
  PASSKEY_ALREADY_EXISTS_MESSAGE,
  PASSKEY_CANCELED_MESSAGE,
  PASSKEY_GENERIC_ERROR_MESSAGE,
} from './EnrollPasskeyModal.constants';

/**
 * Maps a passkey enrollment ceremony error to a display state: the user-facing `message` plus
 * `isAlreadyEnrolled`, which is true for the EXPECTED "this device already has a passkey for this
 * account" outcome (web `InvalidStateError` OR iOS native `matchedExcludedCredential` 1006). That case
 * is informational, not a failure, so the modal renders it neutrally rather than as a red error.
 *
 * The classification (via utils/webAuthn) reads the iOS ASAuthorizationError CODE out of the message,
 * not just the DOMException name — the @capgo native plugin forwards iOS errors with a generic name but
 * the real "(… AuthorizationError error 1006.)" string, so keying on the name alone misses them (DEV-4084).
 *
 * Anything unrecognized falls back to the generic message WITH the `name: message` detail appended — the
 * native Capacitor shell has no console and the App Insights browser SDK is force-disabled there, so that
 * on-screen detail is the only channel exposing WHY an unrecognized failure happened on-device.
 *
 * @param {{ name?: string, message?: string } | unknown} error
 * @returns {{ message: string, isAlreadyEnrolled: boolean }}
 */
export const getEnrollPasskeyErrorState = error => {
  if (isPasskeyAlreadyEnrolledError(error)) {
    return { message: PASSKEY_ALREADY_EXISTS_MESSAGE, isAlreadyEnrolled: true };
  }

  if (isPasskeyCanceledError(error)) {
    return { message: PASSKEY_CANCELED_MESSAGE, isAlreadyEnrolled: false };
  }

  return {
    message: `${PASSKEY_GENERIC_ERROR_MESSAGE} (${error?.name || 'Error'}: ${
      error?.message || String(error)
    })`,
    isAlreadyEnrolled: false,
  };
};
